import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, MapPin, Heart, ArrowRight, Clock, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useRegistrationDeadline } from "@/hooks/useRegistrationDeadline";

interface Event {
  id: string;
  name: string;
  slug: string;
  cause: string;
  location: string;
  event_date: string;
  doors_time: string | null;
  event_time: string | null;
  is_tentative: boolean;
  is_featured: boolean;
  description: string | null;
}

const Events: React.FC = () => {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { data: deadlineData } = useRegistrationDeadline();

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      fetchEvents();
    }
  }, [orgLoading, organization?.id]);

  const fetchEvents = async () => {
    if (!organization?.id) return;
    
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('organization_id', organization.id)
      .order('event_date', { ascending: true });

    if (data && !error) {
      setEvents(data);
    }
    setLoading(false);
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatShortDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const featuredEvent = events.find(e => e.is_featured);
  const upcomingEvents = events.filter(e => !e.is_featured);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading events...</div>
      </div>
    );
  }

  const canonicalUrl = `https://buildwithchrist.ai${location.pathname}`;

  return (
    <div className="relative">
      <Helmet>
        <title>Events | BuildWithChrist.ai</title>
        <meta name="description" content="View upcoming BuildWithChrist.ai build events. Join us for focused 4-hour sessions where we build AI solutions for nonprofits." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* Hero */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">
            Upcoming Events
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join us for focused 4-hour build events where we create real AI solutions 
            for nonprofits serving those most in need.
          </p>
        </div>
      </section>

      {/* Featured Event */}
      {featuredEvent && (
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border-2 border-primary/30 rounded-xl overflow-hidden">
              <div className="bg-primary/10 px-6 py-3 border-b border-primary/20">
                <Badge variant="secondary" className="bg-primary/20 text-primary hover:bg-primary/30">
                  Featured Event
                </Badge>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-4">
                    <h2 className="text-2xl md:text-3xl font-bold font-display">{featuredEvent.name}</h2>
                    <p className="text-muted-foreground">{featuredEvent.description}</p>
                    
                    <div className="flex flex-wrap gap-4 pt-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="text-primary" size={18} />
                        <span className="font-medium">{formatDate(featuredEvent.event_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="text-primary" size={18} />
                        <span>{featuredEvent.location}</span>
                      </div>
                    </div>

                    {(featuredEvent.doors_time || featuredEvent.event_time) && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock size={16} />
                        <span>
                          {featuredEvent.doors_time && `Doors at ${featuredEvent.doors_time}`}
                          {featuredEvent.doors_time && featuredEvent.event_time && ' • '}
                          {featuredEvent.event_time}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-secondary">
                      <Heart size={18} />
                      <span className="font-medium">Supporting: {featuredEvent.cause}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button size="lg" className="w-full" asChild>
                      <Link to="/attendees#register-form">
                        Register to Attend
                        <ArrowRight size={18} className="ml-2" />
                      </Link>
                    </Button>
                    <Button size="lg" variant="outline" className="w-full" asChild>
                      <Link to={`/events/${featuredEvent.slug}`}>View Event Details</Link>
                    </Button>
                    <Button size="lg" variant="ghost" className="w-full" asChild>
                      <Link to="/sponsors">Sponsor This Event</Link>
                    </Button>
                    {deadlineData && !deadlineData.isExpired && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2 mt-3">
                        <Check size={14} className="text-secondary" />
                        Free to attend • Limited space • {deadlineData.urgencyMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="py-12 px-4 bg-card/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold font-display mb-6">More Upcoming Events</h2>
            <div className="space-y-4">
              {upcomingEvents.map(event => (
                <div 
                  key={event.id} 
                  className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">{event.name}</h3>
                        {event.is_tentative && (
                          <Badge variant="outline" className="text-xs">Tentative</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <Calendar size={14} />
                          {formatShortDate(event.event_date)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <MapPin size={14} />
                          {event.location}
                        </span>
                        <span className="flex items-center gap-1.5 text-secondary">
                          <Heart size={14} />
                          {event.cause}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-sm text-muted-foreground">{event.description}</p>
                      )}
                      {deadlineData && !deadlineData.isExpired && !event.is_tentative && (
                        <p className="text-xs text-primary flex items-center gap-1.5 mt-2">
                          <Clock size={12} />
                          Free Entry • {deadlineData.urgencyMessage}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {!event.is_tentative && (
                        <Button size="sm" asChild>
                          <Link to="/attendees#register-form">Register</Link>
                        </Button>
                      )}
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/events/${event.slug}`}>Details</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-display mb-4">
            Want to Stay Updated?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Register for our next event and we'll keep you informed about future events and opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild>
              <Link to="/attendees#register-form">Register to Attend — It's Free</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/nonprofits">Become a Mission Partner</Link>
            </Button>
          </div>
          {deadlineData && !deadlineData.isExpired && (
            <p className="text-sm text-muted-foreground mt-4">
              Limited spots available • {deadlineData.urgencyMessage}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Events;